import { useEffect, lazy, Suspense } from "react";
import { useRoutes, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import ReactGA from "react-ga4";

import Welcome from "../Welcome";
import UnitMenu from "../UnitMenu";
import GameTypeMenu from "../GameTypeMenu";
import FullGameContainer from "../FullGameContainer";
import RedirectPage from "../Redirect";
import EndlessModeMenu from "../EndlessModeMenu";
import {
  ChoosePathRedirect,
  StoryGamesRedirect,
  StoryUnitsRedirect,
} from "../StoryBookCorner/OldStoryRoutes";
// import AnimationTest from "../AnimationTest";
// import FollowStrightLine from "../AFollowStraightLine";
// import HistoryTimeline from "../HistoryTimeline";
import LocationMenu from "../LocationMenu";
import { getSearchParams } from "../nonUIFuncs";
import LazyFallback from "../UI/LazyFallback";

// Pages most players never open download only when they do.
const Dictionary = lazy(() => import("../Dictionary"));
const HistoryTimeline = lazy(() => import("../HistoryTimeline/HistoryTimeline"));
const Credits = lazy(() => import("../Credits"));
const StoryBookCorner = lazy(() => import("../StoryBookCorner"));

// TODO: redirect if a url parameter does not match the data

/**
 * Main application router component that handles all route definitions and navigation.
 * Configures React Router routes for all app pages and tracks page views with Google Analytics.
 *
 * @param {Object} props - Component props
 * @param {Function} props.updateLevelData - Function to update level progression data
 * @param {Array} props.scoreData - Array of player score data
 * @param {Function} props.updateScoreData - Function to update score data
 * @param {Function} props.setShowTopNav - Function to control top navigation visibility
 * @param {string} props.dataset - Current dataset identifier
 * @param {Object} props.config - Application configuration object
 * @param {Array} props.levelData - Array of level progression data
 * @returns {JSX.Element} Configured router with all application routes
 */
const AppRouter = ({
  updateLevelData,
  scoreData,
  updateScoreData,
  setShowTopNav,
  dataset,
  config,
  levelData,
}) => {
  const location = useLocation();
  const params = getSearchParams(location);
  useEffect(() => {
    let paramsToSend = {};
    params.forEach((d) => {
      paramsToSend[d.id] = d.value;
    });
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname,
      params: JSON.stringify(paramsToSend),
    });
  }, [location.pathname]);
  let routesArr = useRoutes([
    {
      path: "/",
      element: <Welcome config={config} />,
    },
    // {
    //   path: "/animation-test",
    //   element: <FollowStrightLine />,
    // },
    {
      path: "/story-book-corner",
      element: (
        <StoryBookCorner config={config} scoreData={scoreData} updateScoreData={updateScoreData} />
      ),
    },
    // the old Story path (choose-path, story units, story games) is folded
    // into Story Book Corner; its addresses redirect (StoryBookCorner/OldStoryRoutes)
    {
      path: "/choose-path/",
      element: <ChoosePathRedirect />,
    },
    {
      path: "/credits/",
      element: <Credits config={config} />,
    },
    {
      path: "/dictionary/",
      element: <Dictionary config={config} dataset={dataset} />,
    },
    {
      path: "/timeline/",
      element: <HistoryTimeline config={config} />,
    },
    ...(config.hasLocationSelection
      ? [
          {
            path: "/choose-location/",
            element: <LocationMenu config={config} />,
          },
        ]
      : []),
    {
      path: "/choose-unit/",
      element: (
        <UnitMenu scoreData={scoreData} config={config} stories={false} dataset={dataset} />
      ),
    },
    {
      path: "/choose-story-unit/",
      element: <StoryUnitsRedirect />,
    },
    {
      path: "/endless-mode/start-game/",
      element: (
        <FullGameContainer
          setShowTopNav={setShowTopNav}
          updateLevelData={updateLevelData}
          updateScoreData={updateScoreData}
          scoreData={scoreData}
          dataset={dataset}
          config={config}
        />
      ),
    },
    {
      path: "/endless-mode/",
      element: (
        <EndlessModeMenu
          scoreData={scoreData}
          levelData={levelData}
          config={config}
        />
      ),
    },
    {
      path: "/choose-game-type/",
      element: (
        <GameTypeMenu scoreData={scoreData} config={config} stories={false} />
      ),
    },
    {
      path: "/story-choose-game-type/",
      element: <StoryGamesRedirect />,
    },
    {
      path: "/start-game/",
      element: (
        <FullGameContainer
          setShowTopNav={setShowTopNav}
          updateLevelData={updateLevelData}
          updateScoreData={updateScoreData}
          scoreData={scoreData}
          dataset={dataset}
          config={config}
        />
      ),
    },
    {
      path: "*",
      element: <RedirectPage />,
    },
  ]);
  return <Suspense fallback={<LazyFallback />}>{routesArr}</Suspense>;
};

export default AppRouter;

AppRouter.propTypes = {
  updateLevelData: PropTypes.func,
  scoreData: PropTypes.array,
  updateScoreData: PropTypes.func,
  setShowTopNav: PropTypes.func,
  dataset: PropTypes.string,
};
